import Image from '@base/components/src/Image';
import Link from '@base/components/src/Link';
import currencyFormatting from '@package/utils/src/currencyFormatting';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import config from 'config-base';
import CustomProductDescription from './__components/CustomProductDescription';
import ProductFee from './__components/ProductFee';
import ProductInfo from './__components/ProductInfo';
import ProductPurchaseLimitMessage from './__components/ProductPurchaseLimitMessage';
import CouponApply from '../CouponApply';
import styles from './ProductCard.module.css';
import { ProductCardProps } from './types';

const ProductStatus = dynamic(() => import('./__components/ProductStatus'));
const CouponApplyErrorMessage = dynamic(() => import('../CouponApplyErrorMessage'));

const ProductCard = ({
  product,
  vendorName,
  className,
  imgLayout,
  productTextClassName,
  onApplyCouponToItem,
  onGetTermsContent,
  regularCouponApplied,
  isReviewOrderPage,
  isShippingOptionPage,
  layout = 'cart',
}: Readonly<ProductCardProps>) => (
  <>
    <figure className={clsx(styles.productCard, { [styles.orderDetailsCard]: layout === 'order-details' }, className)}>
      {product.typeService ? (
        <div
          className={clsx(styles.imageWrapper, {
            [styles.productImageReview]: isReviewOrderPage || isShippingOptionPage,
          })}
        >
          <Image
            data-testid="product-image"
            className={styles.productImage}
            layout={imgLayout || 'intrinsic'}
            src={product.image !== '' ? product.image : '../../../marketplace-static/images/TestGunImage1.png'}
            alt={product.name}
            width={270}
            height={187}
          />
        </div>
      ) : (
        <Link
          data-testid={'product-url'}
          href={product.productUrl}
          classes={clsx(styles.imageWrapper, {
            [styles.productImageReview]: isReviewOrderPage || isShippingOptionPage,
          })}
        >
          <Image
            data-testid="product-image"
            className={styles.productImage}
            layout={imgLayout || 'intrinsic'}
            src={product.image !== '' ? product.image : '../../../marketplace-static/images/TestGunImage1.png'}
            alt={product.name}
            width={270}
            height={187}
          />
        </Link>
      )}

      <figcaption>
        <ProductInfo
          product={product}
          productTextClassName={productTextClassName}
          layout={layout}
          isReviewOrderPage={isReviewOrderPage}
          isShippingOptionPage={isShippingOptionPage}
        />
        <div className={clsx({
          [`${styles.desktop} desktop`]: !isReviewOrderPage && !isShippingOptionPage,
        })}>
          {product.backordered && (
            <ProductStatus dataId="main-list-backordered" title={'Backordered'} stockDateMessage={product.stockDate} />
          )}

          {product.specialOrder && (
            <ProductStatus dataId="main-list-special" title={'Special Order'} stockDateMessage={product.stockDate} />
          )}

          {product.purchaseLimitMessage && (
            <ProductPurchaseLimitMessage testid="product-purchase-message" message={product.purchaseLimitMessage} />
          )}

          {product.superConfigurator && (
            <CustomProductDescription testid="custom-product-description" description={product.productSpecifications} />
          )}
        </div>

        {product.additionalProductFee && (
          <div
            className={clsx(styles.productFee, styles.additionalFeePriceContainer, {
              [styles.hiddenMobile]: product.backordered || product.specialOrder,
            })}
          >
            <ProductFee data={product.additionalProductFee} className={styles.productFeeAdditional} />
            <div className={styles.boxView}>
              <p className={styles.additionalFeePrice}>
                {currencyFormatting(product.additionalProductFee.price, false)}
              </p>
            </div>
          </div>
        )}

        {vendorName && config.brandCode === '0' && !isShippingOptionPage && (
          <div
            className={clsx({
              [styles.hiddenMobile]: !isReviewOrderPage && product.backordered || product.specialOrder,
            })}
            data-testid="product-vendor"
          >
            <p
              className={clsx(styles.vendorDescription, {
                [styles.vendorDetails]: layout === 'order-details',
                [styles.vendorDetailsReview]: isReviewOrderPage,
              })}
            >
              Sold & shipped by
            </p>
            <p
              data-testid="product-vendor-name"
              className={clsx(styles.vendorName, {
                [styles.vendorDetails]: layout === 'order-details',
                [styles.vendorDetailsReview]: isReviewOrderPage,
              })}
            >
              {vendorName}
            </p>
          </div>
        )}
        {layout === 'order-details' && (
          <div
            className={clsx(styles.orderDetails, styles.orderDetailsDesktop, {
              [styles.reviewOrderDesktopView]: isReviewOrderPage || isShippingOptionPage,
            })}
          >
            <div>
              <span
                className={clsx('orderDetailsLabel', {
                  orderDetailsLabelReview: isReviewOrderPage || isShippingOptionPage,
                })}
              >
                Qty:&nbsp;
              </span>
              <span
                className={clsx({
                  orderDetailsPriceReview: isReviewOrderPage || isShippingOptionPage,
                })}
              >
                {' '}
                {product.quantity}
              </span>
            </div>
            <div>
              <span
                className={clsx('orderDetailsLabel', {
                  orderDetailsLabelReview: isReviewOrderPage || isShippingOptionPage,
                })}
              >
                Price:&nbsp;
              </span>
              <span
                className={clsx({
                  orderDetailsPriceReview: isReviewOrderPage || isShippingOptionPage,
                })}
              >
                {currencyFormatting(product.price)}
              </span>
            </div>
          </div>
        )}
        {layout === 'cart' && (
          <div className={styles.couponContainer}>
            {!product.couponAllowed && regularCouponApplied && <CouponApplyErrorMessage message="Coupon Ineligible" />}{' '}
            {product.autoClipCoupon && product.couponAllowed && (
              <CouponApply
                onApplyCouponToItem={onApplyCouponToItem}
                onGetTermsContent={onGetTermsContent}
                productId={product.id}
                autoClipCoupon={product.autoClipCoupon}
              />
            )}
          </div>
        )}
      </figcaption>
    </figure>
    {!isReviewOrderPage && !isShippingOptionPage && (
      <div className={styles.mobileView}>
        {layout === 'cart' && (
          <>
            {!product.couponAllowed && regularCouponApplied && <CouponApplyErrorMessage message="Coupon Ineligible" />}{' '}
            {product.autoClipCoupon && product.couponAllowed && (
              <CouponApply
                onApplyCouponToItem={onApplyCouponToItem}
                onGetTermsContent={onGetTermsContent}
                productId={product.id}
                autoClipCoupon={product.autoClipCoupon}
              />
            )}
          </>
        )}
        {product.backordered && (
          <ProductStatus dataId="main-list-backordered" title={'Backordered'} stockDateMessage={product.stockDate} />
        )}

        {product.specialOrder && (
          <ProductStatus dataId="main-list-special" title={'Special Order'} stockDateMessage={product.stockDate} />
        )}

        {product.purchaseLimitMessage && (
          <ProductPurchaseLimitMessage testid="product-purchase-message" message={product.purchaseLimitMessage} />
        )}

        {product.superConfigurator && (
          <CustomProductDescription testid="custom-product-description" description={product.productSpecifications} />
        )}

        {(product.backordered || product.specialOrder) && (
          <>
            {product.additionalProductFee && (
              <div className={styles.productFee}>
                <ProductFee data={product.additionalProductFee} />
                <div className={styles.boxView}>
                  <span>{currencyFormatting(product.additionalProductFee.price, false)}</span>
                </div>
              </div>
            )}

            {vendorName && config.brandCode === '0' && (
              <div data-testid="product-vendor">
                <span
                  className={styles.vendorDescription}
                >
                  Sold & shipped by{' '}
                </span>
                <span
                  data-testid={'product-vendor-name'}
                  className={styles.vendorName}
                >
                  {vendorName}
                </span>
              </div>
            )}
          </>
        )}
        {layout === 'order-details' && (
          <div className={styles.orderDetails}>
            <div>
              <span className={'orderDetailsLabel'}>Qty:&nbsp;</span>
              <span>{product.quantity}</span>
            </div>
            <div>
              <span className={'orderDetailsLabel'}>Price:&nbsp;</span>
              <span className={'orderDetailsPrice'}>{currencyFormatting(product.price)}</span>
            </div>
          </div>
        )}
      </div>
    )}
  </>
);

export default ProductCard;
